// WP-1.7 (S-39 `/settings`, §18.5 "Categories & Tags: tree editor… bulk re-categorize
// tool", ST-2 tag manager). The full Settings framework (Financial/AI/WhatsApp/Calendars/
// Notifications/Security/Backups/About subnav, S-39..46) lands with WP-1.20 — this WP only
// builds the Categories & Tags section, since it needs its own manager screen now.
import type { CategoryResponse, TagResponse } from '@pfos/shared';
import { ArrowDown, ArrowUp, Tags as TagsIcon } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import {
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
} from '../components/FormKit';
import {
  useCategories,
  useCreateCategory,
  useEditCategory,
  useReassignCategory,
  useReorderCategories,
  useSeedDefaultCategories,
} from '../lib/categories';
import { useCreateTag, useEditTag, useTags } from '../lib/tags';

function moveWithinSiblings(ids: string[], id: string, direction: -1 | 1): string[] {
  const idx = ids.indexOf(id);
  const swapIdx = idx + direction;
  if (idx === -1 || swapIdx < 0 || swapIdx >= ids.length) return ids;
  return ids.map((current, i) => {
    if (i === idx) return ids[swapIdx] ?? current;
    if (i === swapIdx) return ids[idx] ?? current;
    return current;
  });
}

function CategoryRow({
  category,
  siblingIds,
}: {
  category: CategoryResponse;
  siblingIds: string[];
}): React.JSX.Element {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(category.name);
  const editMutation = useEditCategory(category.id);
  const reorderMutation = useReorderCategories();
  const index = siblingIds.indexOf(category.id);

  if (renaming) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <input
          className={`${inputClass} flex-1`}
          aria-label={`Rename ${category.name}`}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          autoFocus
        />
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={editMutation.isPending}
          onClick={() => {
            const name = draft.trim();
            if (name === '') return;
            editMutation.mutate(
              { name },
              {
                onSuccess: () => {
                  setRenaming(false);
                },
              },
            );
          }}
        >
          Save
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 py-1.5 text-sm">
      <span className={category.archived ? 'text-muted line-through' : 'text-ink'}>
        {category.name}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Move ${category.name} up`}
          className="rounded p-1 text-muted hover:bg-bg hover:text-ink disabled:opacity-30"
          disabled={index <= 0}
          onClick={() => {
            reorderMutation.mutate(moveWithinSiblings(siblingIds, category.id, -1));
          }}
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          aria-label={`Move ${category.name} down`}
          className="rounded p-1 text-muted hover:bg-bg hover:text-ink disabled:opacity-30"
          disabled={index === -1 || index >= siblingIds.length - 1}
          onClick={() => {
            reorderMutation.mutate(moveWithinSiblings(siblingIds, category.id, 1));
          }}
        >
          <ArrowDown size={14} />
        </button>
        <button
          type="button"
          aria-label={`Rename ${category.name}`}
          className={ghostButtonInlineClass}
          onClick={() => {
            setDraft(category.name);
            setRenaming(true);
          }}
        >
          Rename
        </button>
        {category.kind === 'expense' && (
          <button
            type="button"
            aria-label={`${category.essential === true ? 'Unmark' : 'Mark'} ${category.name} essential`}
            className={ghostButtonInlineClass}
            disabled={editMutation.isPending}
            onClick={() => {
              editMutation.mutate({ essential: category.essential !== true });
            }}
            title="GL-7/GL-8: essential bills funded before other goals in the AD-2 waterfall"
          >
            {category.essential === true ? 'Essential ✓' : 'Mark essential'}
          </button>
        )}
        <button
          type="button"
          aria-label={`${category.archived ? 'Unarchive' : 'Archive'} ${category.name}`}
          className={ghostButtonInlineClass}
          disabled={editMutation.isPending}
          onClick={() => {
            editMutation.mutate({ archived: !category.archived });
          }}
        >
          {category.archived ? 'Unarchive' : 'Archive'}
        </button>
      </div>
    </div>
  );
}

function TagRow({ tag }: { tag: TagResponse }): React.JSX.Element {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(tag.name);
  const editMutation = useEditTag(tag.id);

  if (renaming) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <input
          className={`${inputClass} flex-1`}
          aria-label={`Rename tag ${tag.name}`}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          autoFocus
        />
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={editMutation.isPending}
          onClick={() => {
            const name = draft.trim();
            if (name === '') return;
            editMutation.mutate(
              { name },
              {
                onSuccess: () => {
                  setRenaming(false);
                },
              },
            );
          }}
        >
          Save
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 py-1.5 text-sm">
      <span className={tag.archived ? 'text-muted line-through' : 'text-ink'}>{tag.name}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Rename tag ${tag.name}`}
          className={ghostButtonInlineClass}
          onClick={() => {
            setDraft(tag.name);
            setRenaming(true);
          }}
        >
          Rename
        </button>
        <button
          type="button"
          aria-label={`${tag.archived ? 'Unarchive' : 'Archive'} tag ${tag.name}`}
          className={ghostButtonInlineClass}
          disabled={editMutation.isPending}
          onClick={() => {
            editMutation.mutate({ archived: !tag.archived });
          }}
        >
          {tag.archived ? 'Unarchive' : 'Archive'}
        </button>
      </div>
    </div>
  );
}

export function SettingsPage(): React.JSX.Element {
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const { data: categories } = useCategories({ kind, includeArchived: true });
  const seedMutation = useSeedDefaultCategories();
  const createMutation = useCreateCategory();
  const [newRootName, setNewRootName] = useState('');

  const reassignMutation = useReassignCategory();
  const [reassignFrom, setReassignFrom] = useState('');
  const [reassignTo, setReassignTo] = useState('');
  const [reassignResult, setReassignResult] = useState<number | null>(null);

  const { data: tags } = useTags({ includeArchived: true });
  const createTagMutation = useCreateTag();
  const [newTagName, setNewTagName] = useState('');

  const roots = (categories ?? [])
    .filter((c) => c.parentId === undefined)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const rootIds = roots.map((c) => c.id);
  const childrenOf = (parentId: string): CategoryResponse[] =>
    (categories ?? [])
      .filter((c) => c.parentId === parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Settings</h1>

      <section className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">
          Categories & Tags
        </p>

        <div className="mb-3 flex gap-2" role="radiogroup" aria-label="Category kind">
          {(['expense', 'income'] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => {
                setKind(k);
              }}
              className={`rounded-[var(--radius-control)] border px-3 py-1.5 text-sm capitalize ${
                kind === k
                  ? 'border-primary bg-primary/10 font-medium text-primary'
                  : 'border-border text-ink hover:bg-bg'
              }`}
            >
              {k}
            </button>
          ))}
        </div>

        {(categories ?? []).length === 0 ? (
          <EmptyState
            icon={TagsIcon}
            message={`No ${kind} categories yet.`}
            action={{
              label: 'Load default categories',
              onClick: () => {
                seedMutation.mutate();
              },
            }}
          />
        ) : (
          <ul className="mb-3 divide-y divide-border">
            {roots.map((root) => {
              const children = childrenOf(root.id);
              return (
                <li key={root.id} className="py-0.5">
                  <CategoryRow category={root} siblingIds={rootIds} />
                  {children.length > 0 && (
                    <ul className="ml-4 divide-y divide-border border-l border-border pl-3">
                      {children.map((child) => (
                        <li key={child.id}>
                          <CategoryRow category={child} siblingIds={children.map((c) => c.id)} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mb-4 flex gap-2">
          <input
            className={`${inputClass} flex-1`}
            aria-label="New category name"
            placeholder="New category name"
            value={newRootName}
            onChange={(e) => {
              setNewRootName(e.target.value);
            }}
          />
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={createMutation.isPending}
            onClick={() => {
              const name = newRootName.trim();
              if (name === '') return;
              createMutation.mutate(
                { name, kind },
                {
                  onSuccess: () => {
                    setNewRootName('');
                  },
                },
              );
            }}
          >
            Add category
          </button>
        </div>

        <div className="rounded-[var(--radius-control)] border border-border p-3">
          <p className="mb-2 text-sm font-medium text-ink">Bulk re-categorize</p>
          <div className="mb-2 flex flex-wrap gap-2">
            <input
              className={`${inputClass} flex-1`}
              aria-label="From category"
              placeholder="From category"
              value={reassignFrom}
              onChange={(e) => {
                setReassignFrom(e.target.value);
              }}
            />
            <input
              className={`${inputClass} flex-1`}
              aria-label="To category"
              placeholder="To category"
              value={reassignTo}
              onChange={(e) => {
                setReassignTo(e.target.value);
              }}
            />
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={reassignMutation.isPending}
              onClick={() => {
                if (reassignFrom.trim() === '' || reassignTo.trim() === '') return;
                setReassignResult(null);
                reassignMutation.mutate(
                  { from: reassignFrom.trim(), to: reassignTo.trim() },
                  {
                    onSuccess: (r) => {
                      setReassignResult(r.updated);
                    },
                  },
                );
              }}
            >
              Reassign
            </button>
          </div>
          {reassignResult !== null && (
            <p className="text-sm text-muted">
              Updated {reassignResult} transaction{reassignResult === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Tags</p>
        {(tags ?? []).length === 0 ? (
          <p className="mb-3 text-sm text-muted">No tags yet.</p>
        ) : (
          <ul className="mb-3 divide-y divide-border">
            {(tags ?? []).map((tag) => (
              <li key={tag.id}>
                <TagRow tag={tag} />
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            className={`${inputClass} flex-1`}
            aria-label="New tag name"
            placeholder="New tag name"
            value={newTagName}
            onChange={(e) => {
              setNewTagName(e.target.value);
            }}
          />
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={createTagMutation.isPending}
            onClick={() => {
              const name = newTagName.trim();
              if (name === '') return;
              createTagMutation.mutate(
                { name },
                {
                  onSuccess: () => {
                    setNewTagName('');
                  },
                },
              );
            }}
          >
            Add tag
          </button>
        </div>
      </section>
    </div>
  );
}
